import { ProductCustomField } from '../ProductCustomField'
import { ProductOptionName } from '../ProductOption'
import { IVariantSubscription } from '../Subscription'


import { Customization } from './Customization'
import PRODUCT_VARIANT_DOMAIN_TYPES from './ProductVariantDomainTypes'
import { PromoConfig } from './PromoConfig'

import type { SvdVariantAttributeValue, SvdVariantMedia, SvdVariantTag } from '../svd/svd-product.types'
// @i18n-ignore-start
export const PRODUCT_VARIANT_STATUS = {
  ACTIVE: 'ACTIVE',
  RETIRED: 'RETIRED',
  DEVELOPMENT: 'DEVELOPMENT',
}
// @i18n-ignore-end

export interface ILocalizedColorGrouping {
  value: string
  displayName: string
}

export interface IBaseProductVariant {
  readonly id: number
  readonly options: ProductVariantOption[]
  readonly price: number
  readonly title: string
  readonly svid: number
  readonly spid: number
  readonly sku: string
  readonly productId?: number
  readonly customFields?: ProductCustomField[] | undefined
  readonly status: keyof typeof PRODUCT_VARIANT_STATUS
  readonly vendorType?: string
  readonly giftCardCredit?: number
  readonly savings?: string
  readonly colorGrouping?: string | null
  readonly localizedColorGrouping?: ILocalizedColorGrouping | null
  readonly servings?: string
  readonly pricePerServing?: number
  readonly traditionalPricePerServing?: number
  readonly promoConfig?: PromoConfig[] | null
  readonly lastChance?: string | null
  readonly customization?: Customization | null
  readonly subscription?: IVariantSubscription
  // SVD variant-level fields (raw SAND shape; undefined for legacy variants).
  /** Per-variant VDA map — `attributeValues[vdaKey].key` is what the SVD flow matches selections against. */
  readonly attributeValues?: Record<string, SvdVariantAttributeValue>
  readonly tags?: SvdVariantTag[]
  /** Variant-level media — asset-id arrays (different shape from the product-level asset-id map). */
  readonly media?: SvdVariantMedia
}

export default class BaseProductVariant implements IBaseProductVariant {
  readonly id: number
  readonly options: ProductVariantOption[]
  readonly price: number
  readonly title: string
  readonly productVariantDomainTypes: PRODUCT_VARIANT_DOMAIN_TYPES
  readonly svid: number
  readonly spid: number
  readonly sku: string
  readonly productId?: number
  readonly customFields?: ProductCustomField[] | undefined
  readonly status: keyof typeof PRODUCT_VARIANT_STATUS
  readonly vendorType?: string
  readonly giftCardCredit?: number
  readonly savings?: string
  readonly colorGrouping?: string | null
  readonly localizedColorGrouping?: ILocalizedColorGrouping | null
  readonly servings?: string
  readonly pricePerServing?: number
  readonly traditionalPricePerServing?: number
  readonly promoConfig?: PromoConfig[] | null
  readonly lastChance?: string | null
  readonly customization?: Customization | null
  readonly subscription?: IVariantSubscription
  readonly attributeValues?: Record<string, SvdVariantAttributeValue>
  readonly tags?: SvdVariantTag[]
  readonly media?: SvdVariantMedia
  constructor(
    {
      id,
      options,
      price,
      title,
      svid,
      spid,
      sku,
      productId,
      customFields = [],
      status,
      vendorType,
      giftCardCredit,
      savings,
      colorGrouping,
      localizedColorGrouping,
      servings,
      pricePerServing,
      traditionalPricePerServing,
      promoConfig = [],
      lastChance,
      customization,
      subscription,
      attributeValues,
      tags,
      media,
    }: IBaseProductVariant,
    productVariantDomainTypes: PRODUCT_VARIANT_DOMAIN_TYPES,
  ) {
    this.svid = svid
    this.spid = spid
    this.sku = sku
    this.productId = productId

    this.id = id
    this.options = options
    this.price = price
    this.title = title
    this.productId = productId
    this.customFields = customFields
    this.status = status
    this.productVariantDomainTypes = productVariantDomainTypes
    this.vendorType = vendorType
    this.giftCardCredit = giftCardCredit
    this.savings = savings
    this.colorGrouping = colorGrouping
    this.localizedColorGrouping = localizedColorGrouping
    this.servings = servings
    this.pricePerServing = pricePerServing
    this.traditionalPricePerServing = traditionalPricePerServing
    this.promoConfig = promoConfig
    this.lastChance = lastChance
    this.customization = customization
    this.subscription = subscription
    this.attributeValues = attributeValues
    this.tags = tags
    this.media = media
    return {
      ...this,
    }
  }
}

export interface IProductVariantOption {
  readonly name: ProductOptionName
  readonly value: string
  readonly displayName?: string
}

export class ProductVariantOption implements IProductVariantOption {
  readonly name: ProductOptionName
  readonly value: string
  readonly displayName?: string

  constructor({ name, value, displayName }: ProductVariantOption) {
    this.name = name
    this.value = value
    this.displayName = displayName
    return {
      ...this,
    }
  }
}
